/**
 * Component: BrowseLink (widget)
 * SpecReferenceID: BROWSE-V1
 * Requirements: BROWSE-V1-R20, BROWSE-V1-R21, WIDGET-V1-R13
 *
 * Cross-module "View in full guideline" link rendered on the four
 * widget retrieval surfaces (Chat / Galen Search / Semantic Search
 * / Simple Search) on result + citation rows that carry an
 * upstream guideline id (AB#4396).
 *
 * Clicking it does NOT open a new tab or navigate the host page
 * away. It asks the ``SurfaceHost`` wrapper (via
 * ``useWidgetNavigation``) to swap the mounted surface to the
 * Browse viewer *in place*, deep-linked to the cited guideline +
 * section. The host page — the customer's site with its own
 * header/footer chrome — stays put; only the widget's own region
 * changes. ``SurfaceHost`` pushes a browser history entry, so the
 * Back button (or the in-widget "← Back" affordance) returns to
 * the chat / search surface with its React state intact (the
 * conversation, the results — exactly as left).
 *
 * The anchor's ``href`` is a ``#galen-browse=<guidelineId>[.<sectionId>]``
 * fragment so that middle-click / ctrl-click ("open in new tab")
 * still works: a fresh load with that fragment makes ``SurfaceHost``
 * open Browse directly. The normal click ``preventDefault``s the
 * fragment navigation and goes through ``openBrowse`` instead.
 *
 *   - No FeatureFlagContext; the ``browse_links_enabled`` flag is
 *     read from the widget config payload (AB#4396) and passed in
 *     by the host surface via the ``enabled`` prop.
 *   - Usage tracking calls ``trackWidgetUsage`` (matches the
 *     existing search_query / result_expand / link_open events on
 *     the surfaces). Same payload shape as the portal BrowseLink's
 *     trackUsageAction call so COUNTER attribution treats portal +
 *     widget link-opens as the same event class.
 *
 * Renders nothing when:
 *   - ``enabled`` is false (deployment-time flag off, or older
 *     backend that doesn't emit the flag), OR
 *   - ``guidelineId`` is missing or not a finite number (a
 *     citation / result row that doesn't carry a usable id).
 *
 * Risk Links: R7, R11
 * FMEA Links: FM7, FM11
 */

import React, { useCallback } from 'react';

import type { WidgetApiClient } from '../api/client';
import { trackWidgetUsage } from '../core/usage-tracking';
import { useWidgetNavigation } from './navigation-context';

export type BrowseLinkSurfaceKey =
  | 'chat'
  | 'galen-search'
  | 'semantic-search'
  | 'simple-search';

export interface BrowseLinkProps {
  /**
   * BROWSE-V1-R18 / R20 feature flag — when false the
   * component returns null so deployments that disable the
   * link see no affordance at all.
   */
  enabled: boolean;
  /**
   * BROWSE-V1-R21 guideline identifier from the upstream
   * payload. Coerced to a number; the link renders only when
   * it's a finite number (real ids always are).
   */
  guidelineId: number | string | null | undefined;
  /**
   * BROWSE-V1-R21 optional section identifier. When present
   * the Browse viewer deep-links to that section
   * (``#cnt_<sectionId>`` scroll).
   */
  sectionId?: number | string | null;
  /** Widget API client for the ``link_open`` usage event. */
  client: WidgetApiClient;
  /**
   * Surface identifier used as the usage-tracking event's
   * ``surface`` field. Mirrors the portal ``referrer_mode``
   * naming via the LOGGING-V1 attribution pipeline.
   */
  surfaceKey: BrowseLinkSurfaceKey;
  /** Optional COUNTER attribution metadata. */
  documentId?: string | null;
  rankingPosition?: number | null;
  guidelineName?: string | null;
  sectionName?: string | null;
  /** Optional original query text for COUNTER attribution. */
  query?: string;
}

function toFiniteNumber(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Render a single "View in full guideline" link, or nothing when
 * the feature is off / the row carries no usable guideline id.
 */
export const BrowseLink: React.FC<BrowseLinkProps> = ({
  enabled,
  guidelineId,
  sectionId,
  client,
  surfaceKey,
  documentId,
  rankingPosition,
  guidelineName,
  sectionName,
  query,
}) => {
  const nav = useWidgetNavigation();

  const guidelineNum = toFiniteNumber(guidelineId);
  const sectionNum = toFiniteNumber(sectionId);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      // Same usage-tracking event the surfaces fire for other
      // outgoing link clicks, so COUNTER attribution stays
      // consistent across all link-open paths in the widget.
      void trackWidgetUsage(client, surfaceKey, 'link_open', {
        ...(documentId ? { document_id: documentId } : {}),
        ...(rankingPosition != null
          ? { ranking_position: rankingPosition }
          : {}),
        ...(sectionNum != null ? { section_id: sectionNum } : {}),
        ...(guidelineName ? { guideline_name: guidelineName } : {}),
        ...(sectionName ? { section_name: sectionName } : {}),
        ...(guidelineNum != null
          ? { guideline_id: String(guidelineNum) }
          : {}),
        ...(query ? { query_text: query } : {}),
        referrer_mode: surfaceKey,
      });

      // In-place navigation: ask SurfaceHost to swap to the Browse
      // viewer. preventDefault stops the `#galen-browse=` fragment
      // navigation (which is only there as a no-JS / new-tab
      // fallback). If there is no SurfaceHost ancestor (shouldn't
      // happen in a mounted widget — only in isolated tests), let
      // the fragment navigation proceed; a SurfaceHost mounted
      // later would pick it up on load.
      if (nav != null && guidelineNum != null) {
        event.preventDefault();
        nav.openBrowse(guidelineNum, sectionNum);
      }
    },
    [
      nav,
      client,
      surfaceKey,
      documentId,
      rankingPosition,
      sectionNum,
      guidelineName,
      sectionName,
      guidelineNum,
      query,
    ],
  );

  if (!enabled || guidelineNum == null) {
    return null;
  }

  // Fallback fragment href (used for middle-click / ctrl-click /
  // copy-link). SurfaceHost reads `#galen-browse=<g>[.<s>]` on load.
  const href =
    '#galen-browse=' +
    String(guidelineNum) +
    (sectionNum != null ? '.' + String(sectionNum) : '');

  // AB#4397: right-align inside a flex-end wrapper so every surface
  // inherits the layout without per-surface wiring. AB#4398:
  // marginTop dropped — when BrowseLink is placed inside the
  // search-result footer row (which sets its own marginTop), this
  // wrapper's marginTop would double up. Chat citations don't
  // suffer because the `<li>` row already has its own list-item
  // spacing; the inter-element rhythm there comes from the `<ul>`'s
  // gap, not from the link's marginTop.
  return (
    <div
      data-galen-browse-link-row
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      <a
        data-galen-browse-link
        href={href}
        onClick={handleClick}
        aria-label={
          sectionName
            ? `Open "${sectionName}" in the full guideline`
            : 'Open the full guideline'
        }
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.25rem',
          fontSize: '0.8rem',
          color: 'var(--galen-primary, #0f172a)',
          textDecoration: 'underline',
        }}
      >
        View in full guideline
      </a>
    </div>
  );
};
